import baseReducer from 'redux/baseReducer';
import { fromJS, Record, List, Map, mergeDeep } from 'immutable';
import Types from './types';

const CategoriesRecord = Record({
    entities: Map({}),
    ids: List([]),
    isError: false,
    isLoading: false,
    pinned: List([]),
});

export const initialState = fromJS({
    appliedFilter: [],
    categories: CategoriesRecord(),
    columns: [],
    data: [],
    error: [],
    filter: [],
    history: [],
    isEnd: false,
    isLoading: false,
    isStrict: false,
    noResults: false,
    pagination: {},
    product: {
        description: '',
        images: [],
        rankings: [],
    },
    searchQuery: '',
    searchQueryFixed: '',
    selected: [],
    sort: {
        field: 'keyword',
        type: 'asc',
    },
    totalKeywords: 0,
    tracked: [],
});


const fetchCategories = (state) =>
    state.update('categories', (categoriesList) => categoriesList.withMutations((c) => {
        c.set('isError', false).set('isLoading', true);
    }));

const fetchCategoriesFail = (state) => state.update('categories', (categoriesList) => categoriesList.withMutations((c) => {
    c.set('isError', true).set('isLoading', false);
}));

const setCategories = (state, { payload }) => {
    const ids = payload
        .map((category) => category.id);
    const entities = ids.reduce((obj, id) => ({
        ...obj,
        [id]: payload.find((category) => category.id === id)
    }), {});

    return state.update('categories', (categoriesList) => categoriesList.withMutations((c) => {
        c.set('entities', fromJS(entities))
            .set('ids', fromJS(ids))
            .set('isError', false)
            .set('isLoading', false);
    }));
};

const setStrictSearch = (state, { payload }) => state.set('isStrict', payload);

const updateCategories = (state, { payload }) => {
    const newIds = payload
        .map((category) => category.id)
        .filter((id) => !state.getIn(['categories', 'ids']).includes(id));

    const newEntities = newIds.reduce((obj, id) => ({
        ...obj,
        [id]: payload.find((category) => category.id === id)
    }), {});

    return state.update('categories', (categoriesList) => categoriesList.withMutations((c) => {
        c.get('entities').mergeDeep(newEntities);
        c.get('ids').mergeDeep(newIds);
        c.set('isError', false).set('isLoading', false);
    }));
};

const pinCategory = (state, { payload }) => {
    let updatedPins;
    const pinned = state.getIn(['categories', 'pinned']);
    if (payload.get('type') === 'keyword') {
        const newIndex = pinned.findIndex((x) => x.get('type') === 'keyword');
        updatedPins = newIndex < 0 ? pinned.push(payload) : pinned.set(newIndex, payload);
    } else {
        updatedPins = pinned.some((cat) => cat.get('id', cat.get('nodeId')) === payload.get('id', payload.get('nodeId')))
            ? pinned
            : pinned.push(payload);
    }
    return state.setIn(['categories', 'pinned'], updatedPins);
};

const removeCategory = (state, { payload }) => {
    const pinned = state.getIn(['categories', 'pinned']);
    const updatedPins = pinned.filter((pin) => pin.get('id', pin.get('nodeId')) !== payload.get('id', payload.get('nodeId')));
    return state.setIn(['categories', 'pinned'], updatedPins);
};


const reset = (state) => initialState.withMutations((prevState) => {
    prevState
        .set('appliedFilter', state.get('appliedFilter'))
        .set('columns', state.get('columns'))
        .set('filter', state.get('filter'))
        .set('history', state.get('history'))
        .set('searchQuery', state.get('searchQuery'))
        .set('sort', state.get('sort'));
});

const resetAll = () => initialState;

const clearSelected = (state) => state.set('selected', List([]));

const select = (state, { payload }) => {
    if (!state.get('selected').includes(payload)) {
        return state.set('selected', state.get('selected').push(payload));
    }
    return state.set('selected', state.get('selected').filter((x) => x !== payload));
};

const addTracked = (state, { payload }) => state.update('tracked', (trackedList) => trackedList.push(payload));

const addColumns = (state, { payload }) => state.update('columns', (columnsList) => columnsList.push(...payload));

const addBefore = (state, { payload }) => state.update('columns', (columns) => columns.splice(columns.indexOf(payload.item1), 0, payload.item2));

const changeColumnPosition = (state, { payload }) => state.update('columns', (columns) => columns.withMutations((oldColumns) => {
    const oldIndex = oldColumns.indexOf(payload.item2);
    const oldIndex2 = oldColumns.indexOf(payload.item1);
    const temp = oldColumns.get(oldIndex);
    oldColumns.set(oldIndex, oldColumns.get(oldIndex2)).set(oldIndex2, temp);
}));

const removeColumn = (state, { payload }) => {
    const updCols = state.get('columns').filter((c) => c !== payload);
    const updFilters = state.get('filter').map((f) => {
        if (f.get('id') === payload) {
            return fromJS({
                id: f.get('id'),
                values: { max: '', min: '' }
            });
        }
        return f;
    });
    const updAppliedFilters = state.get('appliedFilter').delete(payload);
    return state.withMutations((prevState) => {
        prevState.set('appliedFilter', updAppliedFilters).set('columns', updCols).set('filter', updFilters);
    });
};

const setTotalKeywords = (state, { payload }) => state.set('totalKeywords', payload);

const setColumns = (state, { payload }) => state.set('columns', fromJS(payload));

const setFilter = (state, { payload }) => state.set('filter', fromJS(payload));

const setHistory = (state, { payload }) => state.set('history', fromJS(payload));

const setEnd = (state) => state.set('isEnd', true);

const resetFilter = (state, { payload }) => state.withMutations((prevState) => {
    prevState.update('filter', (filterList) => filterList.map((f) => fromJS({
        id: f.get('id'),
        values: { max: '', min: '' }
    }))).set('appliedFilter', Map());
    if (payload) {
        prevState.setIn(['categories', 'pinned'], List());
    }
});

const applyFilter = (state) => {
    const appliedFilter = {};
    const newColumns = [];
    state.get('filter').filter((f) => f.getIn(['values', 'max']) !== '' || f.getIn(['values', 'min']) !== '')
        .forEach((f) => {
            if (!state.get('columns').includes(f.get('id')) && f.get('id') !== 'wordCount') {
                newColumns.push(f.get('id'));
            }
            appliedFilter[f.get('id')] = {
                max: f.getIn(['values', 'max']),
                min: f.get('id') === 'ranking' && f.getIn(['values', 'min']) === '' ? 1 : f.getIn(['values', 'min']),
            };
        });
    return state.withMutations((prevState) => {
        prevState.set('appliedFilter', fromJS(appliedFilter)).set('columns', mergeDeep(prevState.get('columns'), newColumns));
    });
};

const setSearchQuery = (state, { payload }) => state.set('searchQuery', fromJS(payload));

const setData = (state, { payload }) => state.withMutations((prevState) => {
    prevState.set('data', fromJS(payload)).set('noResults', false).set('searchQueryFixed', state.get('searchQuery'));
});

const setProduct = (state, { payload }) => state.set('product', fromJS(payload));

const updateData = (state, { payload }) => state.withMutations((prevState) => {
    prevState.set('data', mergeDeep(prevState.get('data'), fromJS(payload))).set('noResults', false);
});

const updateHistory = (state, { payload }) => state.withMutations((prevState) => {
    prevState.set('history', mergeDeep(prevState.get('history'), fromJS(payload)));
});

const updatePagination = (state, { payload }) => state.set('pagination', fromJS(payload));

const setError = (state, { payload }) => state.set('error', payload);

const setNoResults = (state) => state.set('noResults', true);

const setLoading = (state, { payload }) => state.withMutations((oldState) => {
    if (payload) {
        oldState.set('isEnd', false);
    }
    oldState.set('isLoading', payload);
});

const setSort = (state, { payload }) => {
    let direction = 'asc';
    if (!payload.type) {
        const sort = state.get('sort');
        if (sort.get('field') === payload.field) {
            if (sort.get('type') === 'asc') {
                direction = 'desc';
            }
        }
    } else {
        direction = payload.type;
    }
    return state.set('sort', fromJS({
        field: payload.field,
        type: direction
    }));
};

export default baseReducer(initialState, {
    [Types.ADD_BEFORE_COLUMN]: addBefore,
    [Types.ADD_COLUMNS]: addColumns,
    [Types.ADD_TRACKED]: addTracked,
    [Types.APPLY_FILTER]: applyFilter,
    [Types.CHANGE_COLUMN_POSITION]: changeColumnPosition,
    [Types.CLEAR_SELECTED]: clearSelected,
    [Types.FETCH_CATEGORIES]: fetchCategories,
    [Types.FETCH_CATEGORIES_FAIL]: fetchCategoriesFail,
    [Types.PIN_CATEGORY]: pinCategory,
    [Types.REMOVE_CATEGORY]: removeCategory,
    [Types.REMOVE_COLUMN]: removeColumn,
    [Types.RESET]: reset,
    [Types.RESET_ALL]: resetAll,
    [Types.RESET_FILTER]: resetFilter,
    [Types.SELECT]: select,
    [Types.SET_CATEGORIES]: setCategories,
    [Types.SET_COLUMNS]: setColumns,
    [Types.SET_DATA]: setData,
    [Types.SET_END]: setEnd,
    [Types.SET_ERROR]: setError,
    [Types.SET_FILTER]: setFilter,
    [Types.SET_HISTORY]: setHistory,
    [Types.SET_LOADING]: setLoading,
    [Types.SET_NO_RESULTS]: setNoResults,
    [Types.SET_PRODUCT]: setProduct,
    [Types.SET_SEARCH_QUERY]: setSearchQuery,
    [Types.SET_SORT]: setSort,
    [Types.SET_STRICT_SEARCH]: setStrictSearch,
    [Types.SET_TOTAL_KEYWORDS]: setTotalKeywords,
    [Types.UPDATE_CATEGORIES]: updateCategories,
    [Types.UPDATE_DATA]: updateData,
    [Types.UPDATE_HISTORY]: updateHistory,
    [Types.UPDATE_PAGINATION]: updatePagination,
});
